/** Quiet gap after the button grid appears, before the beep and recording. */
const HOME_MIC_START_DELAY_MS = 1000;

/** Accept the first Send tap and ignore another while that send is in flight. */
function claimHomeVoiceMemoSend(state) {
  if (!state || state.sending || !state.hasAudio) return false;
  return true;
}

function wavDataUri(samples, sampleRate) {
  const dataSize = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);
  samples.forEach((sample, index) => {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + index * 2, clamped * 0x7fff, true);
  });
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

function homeMicBeepWav() {
  const rate = 8000;
  const count = Math.floor(rate * 0.18);
  const samples = new Array(count);
  for (let i = 0; i < count; i += 1) {
    const env = Math.min(1, i / 80, (count - i) / 80);
    samples[i] = Math.sin((2 * Math.PI * 880 * i) / rate) * 0.9 * env;
  }
  return wavDataUri(samples, rate);
}

const HOME_MIC_BEEP_WAV = homeMicBeepWav();

function audioSession() {
  if (typeof globalThis === 'undefined' || !globalThis.navigator) return null;
  return globalThis.navigator.audioSession || null;
}

function preferSpeakerPlayback() {
  const session = audioSession();
  if (!session) return;
  try {
    session.type = 'playback';
  } catch (error) {
    /* session type is fixed */
  }
}

function isAudioSessionCaptureError(error) {
  const message = String((error && error.message) || error || '');
  return /audio.?session|not compatible with audio capture/i.test(message);
}

/**
 * The beep uses playback so it comes out of the speaker. iOS then rejects
 * getUserMedia until the session allows capture. Set that before opening the mic.
 */
function allowAudioCapture(type) {
  const session = audioSession();
  const next = type || 'play-and-record';
  if (session) {
    try {
      session.type = next;
    } catch (error) {
      if (next !== 'auto') {
        try {
          session.type = 'auto';
        } catch (err) {
          /* session type is fixed */
        }
      }
    }
  }
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/** A one-sample start during the tap lets a later tone play on Android. */
function unlockRunningContext(ctx) {
  try {
    if (
      typeof ctx.createBuffer !== 'function' ||
      typeof ctx.createBufferSource !== 'function'
    ) {
      return;
    }
    const rate = ctx.sampleRate || 8000;
    const buffer = ctx.createBuffer(1, 1, rate);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch (error) {
    /* oscillator and the audio element still carry the tone */
  }
}

/**
 * Short tone about delayMs after the call. The wait is wall time. The context
 * and the beep file are unlocked in this call, while the tap is still active,
 * and the audible beep starts only after that wait on the speaker. Recording
 * must wait until this promise settles. The returned promise has cancel()
 * and cancelled.
 */
function playHomeMicBeep(AudioContextImpl, delayMs, waitFn) {
  const Ctx =
    AudioContextImpl ||
    (typeof globalThis !== 'undefined' &&
      (globalThis.AudioContext || globalThis.webkitAudioContext));
  if (typeof Ctx !== 'function') {
    const skipped = Promise.resolve();
    skipped.cancelled = false;
    skipped.cancel = () => {
      skipped.cancelled = true;
    };
    return skipped;
  }
  const ctx = new Ctx();
  const resumeNow = () => {
    if (typeof ctx.resume !== 'function') return;
    try {
      ctx.resume();
    } catch (error) {
      /* context already running */
    }
  };
  resumeNow();
  preferSpeakerPlayback();
  unlockRunningContext(ctx);

  const delay = Math.max(0, Number(delayMs) || 0);
  let htmlAudio = null;
  const AudioImpl =
    typeof globalThis !== 'undefined' && typeof globalThis.Audio === 'function'
      ? globalThis.Audio
      : null;
  if (AudioImpl && delay > 0) {
    try {
      htmlAudio = new AudioImpl();
      htmlAudio.src = HOME_MIC_BEEP_WAV;
      htmlAudio.volume = 0;
      if (typeof htmlAudio.setAttribute === 'function') {
        htmlAudio.setAttribute('playsinline', 'true');
      }
      const unlocked = htmlAudio.play();
      const hush = () => {
        if (audible) return;
        try {
          htmlAudio.pause();
          htmlAudio.currentTime = 0;
        } catch (error) {
          /* not playing yet */
        }
      };
      if (unlocked && typeof unlocked.then === 'function') unlocked.then(hush, () => {});
      else hush();
    } catch (error) {
      htmlAudio = null;
    }
  }

  const handle = { cancelled: false };
  let settled = false;
  let audible = false;
  let timer = null;
  let releaseWait = null;
  let osc = null;
  const promise = new Promise((resolve) => {
    const finish = () => {
      if (settled) return;
      settled = true;
      const closing = typeof ctx.close === 'function' ? ctx.close() : null;
      Promise.resolve(closing).then(resolve, resolve);
    };
    const playTone = async () => {
      if (handle.cancelled || settled) {
        finish();
        return;
      }
      if (typeof ctx.resume === 'function') {
        try {
          await ctx.resume();
        } catch (error) {
          /* play anyway */
        }
      }
      if (handle.cancelled || settled) {
        finish();
        return;
      }
      preferSpeakerPlayback();
      if (htmlAudio) {
        try {
          audible = true;
          htmlAudio.volume = 1;
          htmlAudio.currentTime = 0;
          const played = htmlAudio.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
          htmlAudio.addEventListener('ended', finish);
        } catch (error) {
          /* oscillator still carries the tone */
        }
      } else if (AudioImpl) {
        try {
          htmlAudio = new AudioImpl();
          htmlAudio.src = HOME_MIC_BEEP_WAV;
          htmlAudio.volume = 1;
          const played = htmlAudio.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
          htmlAudio.addEventListener('ended', finish);
        } catch (error) {
          /* oscillator still carries the tone */
        }
      }
      try {
        osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        const startAt = Number(ctx.currentTime) || 0;
        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(1, startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.18);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.onended = finish;
        osc.start(startAt);
        osc.stop(startAt + 0.18);
      } catch (error) {
        finish();
      }
    };
    const afterDelay = () => {
      playTone();
    };
    if (delay === 0) {
      afterDelay();
    } else if (typeof waitFn === 'function') {
      Promise.resolve(waitFn(delay)).then(afterDelay, afterDelay);
    } else {
      timer = setTimeout(afterDelay, delay);
      releaseWait = () => {
        clearTimeout(timer);
        afterDelay();
      };
    }
    handle.cancel = () => {
      if (settled) return;
      handle.cancelled = true;
      if (typeof releaseWait === 'function') releaseWait();
      if (htmlAudio && typeof htmlAudio.pause === 'function') {
        try {
          htmlAudio.pause();
        } catch (error) {
          /* already stopped */
        }
      }
      if (!osc) {
        finish();
        return;
      }
      try {
        osc.stop();
      } catch (error) {
        finish();
      }
    };
  });
  promise.cancel = () => {
    if (typeof handle.cancel === 'function') handle.cancel();
  };
  Object.defineProperty(promise, 'cancelled', {
    get() {
      return handle.cancelled;
    },
  });
  return promise;
}

/** Recording begins after the beep promise settles, unless it was cancelled. */
function beginHomeMicAfterBeep({ beep, startRecorder }) {
  return Promise.resolve(beep).then(() => {
    if (beep && beep.cancelled) return;
    return startRecorder();
  });
}

const HOME_MIC_HINT_KEY = 'mhome-voice-memo-hint-seen';
const HOME_MIC_IPHONE_HINT_KEY = 'mhome-voice-memo-iphone-hint-seen';

function hintFlagSeen(storage, key) {
  try {
    return storage.getItem(key) === '1';
  } catch {
    return true;
  }
}

function markHintFlag(storage, key) {
  try {
    storage.setItem(key, '1');
  } catch {
    /* storage can be unavailable */
  }
}

function homeMicHintSeen(storage) {
  return hintFlagSeen(storage, HOME_MIC_HINT_KEY);
}

function markHomeMicHintSeen(storage) {
  markHintFlag(storage, HOME_MIC_HINT_KEY);
}

function homeMicIphoneHintSeen(storage) {
  return hintFlagSeen(storage, HOME_MIC_IPHONE_HINT_KEY);
}

function markHomeMicIphoneHintSeen(storage) {
  markHintFlag(storage, HOME_MIC_IPHONE_HINT_KEY);
}

module.exports = {
  HOME_MIC_START_DELAY_MS,
  allowAudioCapture,
  beginHomeMicAfterBeep,
  claimHomeVoiceMemoSend,
  isAudioSessionCaptureError,
  playHomeMicBeep,
  homeMicHintSeen,
  markHomeMicHintSeen,
  HOME_MIC_HINT_KEY,
  homeMicIphoneHintSeen,
  markHomeMicIphoneHintSeen,
  HOME_MIC_IPHONE_HINT_KEY,
};
